import type { Order } from '@/types/commerce'
import { ORDER_STATUSES } from './commerce'

/** Status names accepted by a rule — `'PAST_DUE'`, `'PAUSED'`, … */
export type OrderStatusKey = keyof typeof ORDER_STATUSES

/**
 * Billing-state rule, the sibling axis to `IPlanRule`. The plan rule answers
 * WHICH PRODUCT the user holds; this one answers WHETHER THAT RELATIONSHIP IS
 * HEALTHY. They compose with AND, so "monthly subscribers in good standing" is
 * `plans.inclusive` plus `orderStatus.exclusive`.
 *
 * Evaluated over the FULL order history — unlike `hasPlans`, it does not narrow
 * to active statuses first. A rule on a terminal status (`'DEACTIVATED'`,
 * `'CANCELED'`) therefore matches forever once such an order exists, and the
 * composition is per USER, not per ORDER: `plans.inclusive: ['X']` plus
 * `exclusive: ['DEACTIVATED']` reads as "holds X and isn't deactivated" but
 * means "holds X and has never had any deactivated order, of any product".
 *
 * An absent/empty rule matches everyone.
 */
export interface IOrderStatusRule {
  inclusive?: OrderStatusKey[]
  exclusive?: OrderStatusKey[]
}

// Rules come from remote config, so a name that no longer exists can arrive;
// the widened type keeps the `undefined` case reachable for the compiler.
const STATUS_CODES: Record<string, number | undefined> = ORDER_STATUSES

const listMatches = (list: OrderStatusKey[], orders: Order[]): boolean =>
  list.some((name) => {
    const code = STATUS_CODES[name]
    // Never `if (code)` — `PAUSED` is 0, and truthiness would silently drop it.
    return code !== undefined && orders.some((order) => order?.status === code)
  })

/**
 * True when the orders satisfy the rule: no order carries a status in
 * `exclusive`, and — when `inclusive` is non-empty — at least one order carries
 * a status in it. Mirrors `hasPlans`: `exclusive` is evaluated first, so it
 * wins when both lists match.
 */
export const hasOrderStatus = (orders: Order[] = [], rule: IOrderStatusRule = {}): boolean => {
  const inclusive = (rule.inclusive ?? []).filter(Boolean)
  const exclusive = (rule.exclusive ?? []).filter(Boolean)

  if (!inclusive.length && !exclusive.length) return true

  if (listMatches(exclusive, orders)) return false
  if (inclusive.length && !listMatches(inclusive, orders)) return false

  return true
}
